import { useState } from 'react';

import { Alert, Box, Button, ButtonGroup, Heading, Modal, Text, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { MAX_NUM_MULTIPLEX_UNITS_VALUE } from '@zg-rentals/zillow-rental-manager/constants';
import {
  createEmptyRow,
  getSquareFeetError,
  isDuplicateUnitNumber,
  MAX_UNIT_NUMBER_LENGTH,
  UnitRow,
} from '@zg-rentals/zillow-rental-manager/utils';

import { useMultiplexUnitCreation } from '@zg-rentals/zillow-rental-manager/hooks';

import EditableUnitGrid from './EditableUnitGrid';

import type { UserPropertyMultiplexCreateUnitRequestWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { CreateMultiplexResult } from '@zg-rentals/zillow-rental-manager/hooks';

type Props = {
  isOpen: boolean;
  fullAddress: string;
  numUnits?: string;
  customerId?: string;
  onClose: () => void;
  onConfirm: (result: CreateMultiplexResult) => void;
  isSubmitting?: boolean;
};

const UnitEntryStep = ({
  isOpen,
  fullAddress,
  numUnits,
  customerId,
  onClose,
  onConfirm,
  isSubmitting = false,
}: Props): JSX.Element => {
  const [rows, setRows] = useState<Array<UnitRow>>(() => {
    const count = numUnits ? parseInt(numUnits, 10) : 1;
    const safeCount = Number.isNaN(count) || count < 1 ? 1 : Math.min(count, MAX_NUM_MULTIPLEX_UNITS_VALUE);
    return Array.from({ length: safeCount }, createEmptyRow);
  });
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { existingUnitNumbers, isPending, createMultiplex } = useMultiplexUnitCreation({ fullAddress, customerId });
  const hasSizeError = rows.some((row) => Boolean(getSquareFeetError(row.size)));

  const handleAddRow = () => {
    if (rows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE) return;
    setHasSubmitted(false);
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    const hasErrors = rows.some(
      (row) =>
        !row.unitNumber.trim() ||
        row.unitNumber.length > MAX_UNIT_NUMBER_LENGTH ||
        isDuplicateUnitNumber(row.id, row.unitNumber, rows, existingUnitNumbers) ||
        !row.beds ||
        !row.baths ||
        Boolean(getSquareFeetError(row.size)),
    );
    if (hasErrors) return;

    const units: Array<UserPropertyMultiplexCreateUnitRequestWebDto> = rows.map((row) => ({
      unitNumber: row.unitNumber,
      bedroomCount: row.beds === 'studio' ? 0 : row.beds ? Number(row.beds) : undefined,
      bathroomCount: row.baths ? parseFloat(row.baths) : undefined,
      squareFeet: row.size ? Number(row.size) : undefined,
    }));

    createMultiplex(units, {
      onSuccess: (result) => onConfirm(result),
      onError: (msg: string) => setErrorMessage(msg),
    });
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && !isSubmitting && onClose()}
      size={{ base: 'fullScreen', md: 'xl' }}
      useDismissProps={{ outsidePress: false }}
      header={<Heading level={2}>Add property</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
            <Heading level={3} textStyle="heading-xs">
              Tell us about your units
            </Heading>
            <Text>
              Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your
              perfect renter.
            </Text>
          </Box>

          {existingUnitNumbers.length > 0 &&
            (() => {
              const unitLabel = existingUnitNumbers.length === 1 ? 'Unit' : 'Units';
              const unitListString =
                existingUnitNumbers.length > 1
                  ? `${existingUnitNumbers.slice(0, -1).join(', ')} and ${existingUnitNumbers[existingUnitNumbers.length - 1]}`
                  : existingUnitNumbers[0];
              const verb = existingUnitNumbers.length === 1 ? 'exists' : 'exist';
              return (
                <Alert role="status" tone="info">
                  {unitLabel} <strong>{unitListString}</strong> already {verb} at this property
                </Alert>
              );
            })()}

          {errorMessage && (
            <Alert role="status" tone="critical">
              {errorMessage}
            </Alert>
          )}

          <EditableUnitGrid
            rows={rows}
            onChange={setRows}
            existingUnitNumbers={existingUnitNumbers}
            hasSubmitted={hasSubmitted}
            maxUnits={MAX_NUM_MULTIPLEX_UNITS_VALUE}
          />

          <Button
            data-testid="add-unit-button"
            fluid={false}
            tone="brand"
            emphasis="outlined"
            icon={<IconPlusFilled />}
            disabled={rows.length >= MAX_NUM_MULTIPLEX_UNITS_VALUE}
            onClick={handleAddRow}
            css={{ alignSelf: 'flex-start' }}
          >
            Add unit
          </Button>
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Unit entry actions">
          <TextButton tone="brand" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </TextButton>
          <Button
            data-testid="add-property-button"
            fluid={false}
            tone="brand"
            emphasis="filled"
            loading={isPending || isSubmitting}
            disabled={hasSizeError}
            onClick={handleSubmit}
          >
            Add property
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default UnitEntryStep;
